import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';
import {
  initFirebase,
  isFirebaseReady,
  subscribeToQueue,
  subscribeToDoctorStatuses,
  subscribeToUnreadCount,
  subscribeToNewNotifications,
  subscribeToNotificationChanges,
  disconnectFirebase,
} from '../services/firebase';

const RealtimeContext = createContext();

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

/**
 * RealtimeProvider
 *
 * Manages Firebase Realtime Database connections and provides live data to components:
 * - Queue positions & wait times
 * - Doctor availability statuses
 * - Notification badge counts
 * - New notification alerts
 *
 * Falls back to API polling when Firebase is not configured.
 */
export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();

  // State
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [queue, setQueue] = useState([]);
  const [doctorStatuses, setDoctorStatuses] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected | connecting | connected | polling

  // Refs for cleanup
  const unsubscribers = useRef([]);
  const pollingInterval = useRef(null);

  // Derive adminId from user
  const adminId = user?.admin_id || user?.user_id;
  const userId = user?.user_id;
  const userRole = user?.role;

  /**
   * Initialize Firebase from API config
   */
  const initializeFirebase = useCallback(async () => {
    if (!adminId || !userRole) return;

    setConnectionStatus('connecting');

    try {
      // Fetch Firebase config from backend
      const rolePrefix = userRole === 'patient' ? 'user' : userRole;
      const response = await api.get(`/api/${rolePrefix}/realtime/firebase-config`);

      if (response?.data?.configured && response.data.config) {
        const success = initFirebase(response.data.config);
        if (success) {
          setFirebaseReady(true);
          setConnectionStatus('connected');
          return true;
        }
      }
    } catch (error) {
      console.warn('Firebase config not available, using polling fallback');
    }

    // Fallback to polling
    setFirebaseReady(false);
    setConnectionStatus('polling');
    return false;
  }, [adminId, userRole]);

  /**
   * Setup Firebase real-time listeners
   */
  const setupListeners = useCallback(() => {
    if (!isFirebaseReady() || !adminId || !userId) return;

    // Clean up previous listeners
    unsubscribers.current.forEach((unsub) => unsub());
    unsubscribers.current = [];

    // Queue listener (today's queue)
    const today = new Date().toISOString().split('T')[0];
    const unsubQueue = subscribeToQueue(adminId, today, (queueData) => {
      setQueue(queueData);
    });
    unsubscribers.current.push(unsubQueue);

    // Doctor statuses listener
    if (['admin', 'staff', 'doctor'].includes(userRole)) {
      const unsubDoctors = subscribeToDoctorStatuses(adminId, (statuses) => {
        setDoctorStatuses(statuses);
      });
      unsubscribers.current.push(unsubDoctors);
    }

    // Unread notification count
    const unsubUnread = subscribeToUnreadCount(adminId, userId, (count) => {
      setUnreadCount(count);
    });
    unsubscribers.current.push(unsubUnread);

    // New notifications
    const unsubNewNotif = subscribeToNewNotifications(adminId, userId, (notification) => {
      setNotifications((prev) => {
        // Avoid duplicates
        if (prev.some((n) => n.key === notification.key)) return prev;
        return [notification, ...prev].slice(0, 50); // Keep last 50
      });
    });
    unsubscribers.current.push(unsubNewNotif);

    // Notification changes (read status)
    const unsubNotifChange = subscribeToNotificationChanges(adminId, userId, (updated) => {
      setNotifications((prev) => prev.map((n) => (n.key === updated.key ? { ...n, ...updated } : n)));
    });
    unsubscribers.current.push(unsubNotifChange);
  }, [adminId, userId, userRole]);

  /**
   * Polling fallback for when Firebase is not available
   */
  const startPolling = useCallback(() => {
    if (!adminId || !userRole) return;
    if (pollingInterval.current) return; // Already polling

    const rolePrefix = userRole === 'patient' ? 'user' : userRole;

    const poll = async () => {
      try {
        // Fetch queue
        const today = new Date().toISOString().split('T')[0];
        const queueRes = await api.get(`/api/${rolePrefix}/realtime/queue?date=${today}`);
        if (queueRes?.data?.queue) {
          setQueue(queueRes.data.queue);
        }

        // Fetch unread count
        const countRes = await api.get(`/api/${rolePrefix}/realtime/notifications/unread-count`);
        if (countRes?.data) {
          setUnreadCount(countRes.data.unread_count || 0);
        }

        // Fetch doctor statuses (admin/staff/doctor only)
        if (['admin', 'staff', 'doctor'].includes(userRole)) {
          const statusRes = await api.get(`/api/${rolePrefix}/realtime/doctor-statuses`);
          if (statusRes?.data) {
            // Convert array to object keyed by doctor_id
            const statusMap = {};
            if (Array.isArray(statusRes.data)) {
              statusRes.data.forEach((doc) => {
                statusMap[doc.doctor_id] = doc;
              });
            } else {
              Object.assign(statusMap, statusRes.data);
            }
            setDoctorStatuses(statusMap);
          }
        }
      } catch (error) {
        // Silently handle polling errors - don't spam console
        if (error.message !== 'Session expired. Please login again.') {
          console.debug('Realtime polling error:', error.message);
        }
      }
    };

    // Initial poll
    poll();
    // Poll every 30 seconds
    pollingInterval.current = setInterval(poll, 30000);
  }, [adminId, userRole]);

  const stopPolling = useCallback(() => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  }, []);

  /**
   * Mark notifications as read
   */
  const markNotificationsRead = useCallback(
    async (notificationKey = null) => {
      if (!userRole) return;

      try {
        const rolePrefix = userRole === 'patient' ? 'user' : userRole;
        const body = notificationKey ? { notification_key: notificationKey } : {};
        await api.post(`/api/${rolePrefix}/realtime/notifications/mark-read`, body);

        if (notificationKey) {
          // Mark single as read
          setNotifications((prev) => prev.map((n) => (n.key === notificationKey ? { ...n, read: true } : n)));
          setUnreadCount((prev) => Math.max(0, prev - 1));
        } else {
          // Mark all as read
          setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
          setUnreadCount(0);
        }
      } catch (error) {
        console.error('Failed to mark notifications as read:', error);
      }
    },
    [userRole]
  );

  /**
   * Refresh queue data manually
   */
  const refreshQueue = useCallback(
    async (date = null) => {
      if (!userRole || !adminId) return;

      try {
        const rolePrefix = userRole === 'patient' ? 'user' : userRole;
        const queryDate = date || new Date().toISOString().split('T')[0];
        const res = await api.get(`/api/${rolePrefix}/realtime/queue?date=${queryDate}`);
        if (res?.data?.queue) {
          setQueue(res.data.queue);
        }
      } catch (error) {
        console.error('Failed to refresh queue:', error);
      }
    },
    [userRole, adminId]
  );

  // Initialize on user login — use primitive deps (NOT user object) to avoid re-firing
  const initRef = useRef(false);
  useEffect(() => {
    if (!userId || !adminId) {
      // User logged out — clean up
      initRef.current = false;
      unsubscribers.current.forEach((unsub) => unsub());
      unsubscribers.current = [];
      stopPolling();
      disconnectFirebase();
      setFirebaseReady(false);
      setConnectionStatus('disconnected');
      setQueue([]);
      setDoctorStatuses({});
      setUnreadCount(0);
      setNotifications([]);
      return;
    }

    // Guard against StrictMode double-fire
    if (initRef.current) return;
    initRef.current = true;

    let mounted = true;

    const init = async () => {
      const fbReady = await initializeFirebase();
      if (!mounted) return;

      if (fbReady) {
        setupListeners();
      } else {
        startPolling();
      }
    };

    init();

    return () => {
      mounted = false;
      unsubscribers.current.forEach((unsub) => unsub());
      unsubscribers.current = [];
      stopPolling();
    };
  }, [userId, adminId, userRole, initializeFirebase, setupListeners, startPolling, stopPolling]);

  // Memoize context value to prevent cascading re-renders
  const value = useMemo(
    () => ({
      // Connection
      firebaseReady,
      connectionStatus,

      // Queue
      queue,
      refreshQueue,

      // Doctor statuses
      doctorStatuses,

      // Notifications
      unreadCount,
      notifications,
      markNotificationsRead,
    }),
    [
      firebaseReady,
      connectionStatus,
      queue,
      refreshQueue,
      doctorStatuses,
      unreadCount,
      notifications,
      markNotificationsRead,
    ]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};

export default RealtimeContext;
